<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <b-card>
        <b-row>
          <b-col class="mb-3">
            <h4>{{ $t('nav.detection_sets_list_title') }}</h4>
          </b-col>
          <b-col md="auto">
            <b-button-group>
              <b-button :disabled="loading" v-if="list.actions && list.actions.create" class="mb-3" variant="primary"
                :to="{ name: 'DetectionSetCreate' }">
                {{ $t('detection_set.create_new') }}
              </b-button>
            </b-button-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <DebouncedSearch v-model="filters.search" :placeholder="$t('global.search')" />
          </b-col>
          <b-col md="auto">
            <b-form-group label-cols :label="$dt('global.per_page', 'Per page:')">
              <b-form-select :options="[2, 10, 20, 50, 100, 500]" v-model="filters.per_page" />
            </b-form-group>
          </b-col>
          <b-col md="auto" v-if="listPaginator.last_page > 1">
            <Pagination :data="listPaginator" :limit="5" @pagination-change-page="onPageChange" />
          </b-col>
        </b-row>
        <div v-if="loading && !tableItems.length" class="text-center">
          <b-spinner type="grow" variant="primary" />
        </div>
        <NoDataView v-else-if="!tableItems.length" />
        <b-table v-else class="table-sm text-center" responsive outlined hover head-variant="light" :busy="loading"
          :no-local-sorting=true :sort-by.sync="filters.sort_field" :sort-desc.sync="filters.sort_order"
          :fields="tableFields" :items="tableItems">
          <template v-slot:cell(tags_data)="row">
            <b-badge v-for="tag in row.item.tags_data" :key="tag.id" class="mx-1">{{ tag.name }}</b-badge>
          </template>
          <template v-slot:cell(created_at)="{ value }">{{ momentf(value) }}</template>
          <template v-slot:cell(files_sum_size)="{ value }">{{ prettyBytes(value) }}</template>
          <template v-slot:cell(label_sets)="{ value, item }">
            <router-link v-for="label_set in value" :key="`${item.id}-${label_set.id}`"
              :to="{ name: 'LabelSetSamples', params: { project_id: activeProjectId, label_set_id: label_set.id } }">
              <b-badge v-if="label_set.actions.view" class="mx-1">
                {{ label_set.id }}: {{ label_set.name }}
              </b-badge>
            </router-link>
          </template>
          <template v-slot:cell(options)="row">
            <div class="flex-container options-center">
              <router-link v-if="row.item.actions.update"
                :to="{ name: 'DetectionSetUpload', params: { project_id: row.item.project_id, id: row.item.id } }">
                <a><i class="mx-1 fa fa-upload fa-lg text-warning" /></a>
              </router-link>
              <router-link v-if="row.item.actions.view"
                :to="{ name: 'Detections', params: { project_id: row.item.project_id, detection_set_id: row.item.id } }">
                <a><i class="mx-1 fa fa-eye fa-lg text-success" /></a>
              </router-link>
              <router-link v-if="row.item.actions.update"
                :to="{ name: 'DetectionSetUpdate', params: { project_id: row.item.project_id, id: row.item.id } }">
                <a><i class="mx-1 fa fa-edit fa-lg" /></a>
              </router-link>
              <router-link v-if="row.item.actions.delete"
                :to="{ name: 'DetectionSetDelete', params: { project_id: row.item.project_id, id: row.item.id } }">
                <a><i class="fa fa-trash fa-danger fa-lg" /></a>
              </router-link>
            </div>
          </template>
        </b-table>
        <b-row>
          <!--Paginator at bottom-->
          <b-col v-if="tableItems.length">
            <Pagination :data="listPaginator" :limit="5" @pagination-change-page="onPageChange" />
          </b-col>
        </b-row>
      </b-card>
    </div>
  </div>
</template>

<script>
import Pagination from 'laravel-vue-pagination';
import { mapState } from 'vuex';
import prettyBytes from 'pretty-bytes';
import NoDataView from '@/views/Shared/NoDataView.vue';
import Services from '@/services/index';
import DebouncedSearch from '../Shared/DebouncedSearch.vue';

export default {
  mounted() {
    this.getData();
  },
  props: ['page'],
  data() {
    return {
      tableFields: [
        {
          key: 'id',
          sortable: true,
          label: this.$t('global.id')
        },
        {
          key: 'name',
          sortable: true,
          label: this.$t('detection_set.name'),
          tdClass: 'text-left',
          thClass: 'text-left'
        },
        {
          key: 'files_sum_size',
          sortable: true,
          label: this.$dt('detection_set.size', 'Size'),
          tdClass: 'text-right',
          thClass: 'text-right'
        },
        {
          key: 'created_at',
          sortable: true,
          label: this.$t('global.created_at'),
          tdClass: 'text-right',
          thClass: 'text-right'
        },
        {
          key: 'options',
          label: this.$t('global.options')
        }
      ],
      filters: {
        sort_field: 'id',
        sort_order: true,
        search: '',
        per_page: 20,
      },
      loading: false,
      list: {},
    };
  },
  components: {
    Pagination,
    NoDataView,
    DebouncedSearch
  },
  computed: {
    listPaginator() {
      return Object.is(this.list.meta, undefined) ? {} : this.list.meta;
    },
    tableItems() {
      return this.list.data || [];
    },
    params() {
      return {
        ...this.filters,
        page: this.page,
        sort_order: this.filters.sort_order ? 'desc' : 'asc'
      };
    },
    ...mapState('Auth', ['activeProjectId']),
  },
  watch: {
    page: 'getData',
    filters: { deep: true, handler: 'getData' },
    activeProjectId: function () {
      if (this.activeProjectId) this.getData();
    },
  },
  methods: {
    prettyBytes(value, ...attrs) {
      if (value === null) return '';
      return prettyBytes(Number(value), ...attrs);
    },
    getData() {
      this.loading = true;
      Services.detectionSets.list(this.activeProjectId, this.params).then(({ data }) => {
        this.list = data;
        this.loading = false;
        if (!this.list.data?.length && this.page !== 1) {
          this.onPageChange(1);
        }
      }).catch((err) => {
        if (err.code !== 'ERR_CANCELED') {
          this.loading = false;
          console.log(err);
        }
      });
    },
    onPageChange(page) {
      page = page || this.page;
      if (page !== this.page) this.$router.push({ name: 'DetectionSets', params: { page } });
    },
  }
};
</script>

<style scoped></style>
